import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "next-themes";
import { Menu, X, Sun, Moon, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../../data/site";
import { scrollToId } from "../../lib/lenis";
import { EASE } from "../../lib/motion";

const navCls = (active) =>
  `rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
    active ? "bg-surface text-foreground" : "text-muted-foreground hover:text-foreground"
  }`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goHash = (e, link) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") scrollToId(link.hash);
    else {
      navigate("/");
      setTimeout(() => scrollToId(link.hash), 450);
    }
  };

  const isActive = (l) =>
    l.hash ? false : l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "border-b border-border bg-background/80 shadow-[0_1px_24px_-8px_rgba(15,23,42,0.15)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="wrap flex h-[72px] items-center justify-between" aria-label="Navegación principal">
        <Link to="/" data-testid="nav-logo-link" aria-label="DASOFT — Inicio">
          <Logo />
        </Link>

        <div className="hidden items-center gap-0.5 xl:flex">
          {NAV_LINKS.map((l) =>
            l.hash ? (
              <a
                key={l.label}
                href={`/#${l.hash}`}
                onClick={(e) => goHash(e, l)}
                className={navCls(false)}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.label}
                to={l.to}
                className={() => navCls(isActive(l))}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </NavLink>
            ),
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            data-testid="theme-toggle-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 text-muted-foreground transition-colors duration-200 hover:text-foreground"
          >
            {theme === "dark" ? <Sun className="h-4.5 w-4.5 h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <Link
            to="/contacto"
            data-testid="nav-demo-cta"
            className="hidden items-center rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground shadow-glow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-strong md:inline-flex"
          >
            Solicitar Demo
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 text-foreground xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="overflow-hidden border-t border-border bg-background/95 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="wrap flex flex-col gap-1 py-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35, ease: EASE }}
                >
                  {l.hash ? (
                    <a
                      href={`/#${l.hash}`}
                      onClick={(e) => goHash(e, l)}
                      className="block rounded-xl px-3 py-2.5 text-base font-medium text-foreground"
                      data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                    >
                      {l.label}
                    </a>
                  ) : (
                    <NavLink
                      to={l.to}
                      className={({ isActive: a }) =>
                        `block rounded-xl px-3 py-2.5 text-base font-medium ${a ? "bg-surface text-foreground" : "text-muted-foreground"}`
                      }
                      data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                    >
                      {l.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
              <Link
                to="/contacto"
                data-testid="mobile-demo-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-brand-foreground"
              >
                Solicitar Demo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
